import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useZoomPan } from '../../hooks/useZoomPan';

// Set up the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PdfViewerProps {
  url: string;
  filename: string;
  className?: string;
  onZoomChange?: (zoom: number, zoomIn: () => void, zoomOut: () => void, resetZoom: () => void) => void;
}

export function PdfViewer({ url, filename: _filename, className = '', onZoomChange }: PdfViewerProps) {
  const renderContainerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [renderedPages, setRenderedPages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    zoom,
    position,
    isDragging,
    isPanMode,
    containerRef,
    contentRef,
    zoomIn,
    zoomOut,
    resetZoom,
    handlers,
  } = useZoomPan({ minZoom: 0.5, maxZoom: 3, zoomStep: 0.25 });

  // Notify parent of zoom state changes
  useEffect(() => {
    onZoomChange?.(zoom, zoomIn, zoomOut, resetZoom);
  }, [zoom, zoomIn, zoomOut, resetZoom, onZoomChange]);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      if (!url) return;

      setIsLoading(true);
      setError(null);
      setRenderedPages([]);

      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdfDoc = await loadingTask.promise;

        if (cancelled) return;

        setPdf(pdfDoc);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          setError('PDF konnte nicht geladen werden');
          setIsLoading(false);
        }
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Render all pages when PDF is loaded
  useEffect(() => {
    if (!pdf || !renderContainerRef.current) return;

    let cancelled = false;

    async function renderAllPages() {
      if (!pdf) return;

      const pages: string[] = [];
      const containerWidth = renderContainerRef.current?.clientWidth || 600;
      const pageWidth = containerWidth - 48; // Account for padding

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        if (cancelled) break;

        try {
          const page = await pdf.getPage(pageNum);

          // Create a canvas for this page
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) continue;

          // Calculate scale to fit width
          const viewport = page.getViewport({ scale: 1 });
          const scale = pageWidth / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          // Set canvas dimensions
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          // Render the page
          await page.render({
            canvasContext: context,
            viewport: scaledViewport,
            canvas: canvas,
          }).promise;

          // Convert to data URL and store
          pages.push(canvas.toDataURL('image/png'));
        } catch (err) {
          console.error(`Error rendering page ${pageNum}:`, err);
        }
      }

      if (!cancelled) {
        setRenderedPages(pages);
        setIsLoading(false);
      }
    }

    renderAllPages();

    return () => {
      cancelled = true;
    };
  }, [pdf]);

  if (error) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="mt-4 text-gray-900 font-medium">Fehler beim Laden</p>
          <p className="mt-1 text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={renderContainerRef} className={`h-full overflow-hidden bg-gray-200 ${className}`}>
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">PDF wird geladen...</p>
          </div>
        </div>
      ) : (
        <div
          ref={containerRef}
          className={`h-full overflow-auto ${zoom > 1 && isPanMode ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
          onMouseDown={handlers.onMouseDown}
          onMouseMove={handlers.onMouseMove}
          onMouseUp={handlers.onMouseUp}
          onMouseLeave={handlers.onMouseLeave}
          onWheel={handlers.onWheel}
        >
          <div
            ref={contentRef}
            className={`flex flex-col items-center gap-4 p-4 ${isDragging ? '' : 'transition-transform duration-150'}`}
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transformOrigin: 'center top',
            }}
          >
            {renderedPages.map((pageDataUrl, index) => (
              <div key={index} className="shadow-lg">
                <img
                  src={pageDataUrl}
                  alt={`Seite ${index + 1}`}
                  className="bg-white"
                  style={{ display: 'block' }}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
